import { Gift, Layers3 } from 'lucide-react';
import Image from 'next/image';
import React, { useState } from 'react';
import PayoutTermMoreMenu from 'src/modules/conversions/components/payout-terms/PayoutTermMoreMenu';
import { Conversion, PayoutCalculationStrategy } from 'src/modules/conversions/domain';
import { PayeeType, PayoutScheme, PayoutTermDTO, PayoutTermType } from 'src/modules/conversions/infra/dtos';
import { useCurrency } from 'src/modules/currencies';
import { CurrencyIcon } from 'src/modules/currencies/components/CurrencyIcon';

import TemporaryDrawer from '@/components/TemporaryDrawer';
import { Badge, Button, Card, CardContent, Skeleton, Text, Tooltip } from '@/design-system/components/ui';
import { maskAddressValue } from '@/utils/format';

import { FormattedAmount } from './FormattedAmount';
import { PayoutAmounts } from './PayoutAmounts';
import { PayoutCaps } from './PayoutCaps';
import { PayoutTiers } from './PayoutTiers';

// TODO:
// Cambiar para que el componente solo acepte los props que necesita sin necesidad de tener que pasarle todo el objeto completo
// de conversion y todo el objeto de payout term.
interface Props {
  index?: number;
  conversion: Conversion;
  payoutTerm: PayoutTermDTO;
}

const PayoutTermCard = ({ index, conversion, payoutTerm }: Props) => {
  const [showDrawer, setShowDrawer] = useState(false);

  const {
    scheme,
    payout_currency_address: payoutCurrencyAddress,
    payout_currency_chain_id: payoutCurrencyChainId,
    base_currency: payoutBaseCurrency,
    type,
    pool_calculation_day_cron,
    pool_duration,
    payee_type: payeeType,
  } = payoutTerm;
  const isPoolPayout = scheme == PayoutScheme.Pool;

  const isFixedPayment = payoutTerm.calculation_strategy === PayoutCalculationStrategy.Fixed;
  const isTiered = payoutTerm.payout_groups.length > 0;
  const isMultilevel = Boolean(payoutTerm.referrer2_amount);
  const isRequiredApproval = payoutTerm.require_approval;
  const isCapped = Boolean(payoutTerm.wallet_cap_amount) || Boolean(payoutTerm.end_user_cap_amount);
  const isPoints = payoutTerm.type === PayoutTermType.Point;
  const hasExtraInfo = Boolean(payoutTerm.wallet_cap_amount) || Boolean(payoutTerm.end_user_cap_amount) || isTiered;

  const isPointsReward = type === 'point';

  const { data: currency, isLoading: isCurrencyLoading } = useCurrency(
    isPointsReward ? undefined : payoutCurrencyAddress,
    isPointsReward ? undefined : payoutCurrencyChainId?.toString(),
  );

  const currencySymbol = isPointsReward ? 'POINTS' : (currency?.name ?? maskAddressValue(payoutCurrencyAddress));
  const payoutCurrencySymbol = currencySymbol;
  const payoutCurrencyDecimals = isPointsReward ? 0 : (currency?.decimals ?? 18);
  const payoutCurrencyLogoUri = isPointsReward ? null : (currency?.logoUri ?? null);

  const indexText = index ? `#${index}` : '';

  const requireAffiliate = payoutTerm.require_affiliate;

  const hasPayoutBaseCurrency = !!payoutBaseCurrency && payoutBaseCurrency.toLocaleLowerCase() != 'none';
  const payoutBaseCurrencySymbol = hasPayoutBaseCurrency ? payoutBaseCurrency?.toUpperCase() : payoutCurrencySymbol;

  const tooltipTitle = requireAffiliate
    ? isPoints
      ? `Affiliates and end users will only receive points when an affiliate is present`
      : "Payouts will only occur if there's a referral in place"
    : `End users will always receive ${isPoints ? 'points' : 'payouts'} and affiliates only if it corresponds`;
  const tooltipRequire = 'Automatic payouts are disabled';

  const drawerButtonLabel = `View
  ${isTiered ? (isPoolPayout ? 'Multipliers' : 'Tiers') : ''}
  ${isTiered && isCapped ? 'and' : ''}
  ${isCapped ? 'Caps' : ''}`;

  const drawerDescriptionLabel = `Reward
  ${isTiered ? (isPoolPayout ? 'multipliers' : 'tiers') : ''}
  ${isTiered && isCapped ? 'and' : ''}
  ${isCapped ? 'caps' : ''}`;

  return (
    <>
      {/*
  TODO:
  Mover este Drawer fuera del componente card para no crear 1 por cada card, simplemente colocar
  un drawer y pasarle data por context.
  */}
      <TemporaryDrawer
        open={showDrawer}
        onClose={() => setShowDrawer(false)}
        title={`Reward ${indexText}`}
        subtitle={drawerDescriptionLabel}
        icon={<Gift style={{ color: '#2784dd' }} />}
      >
        <div className="tw:flex tw:flex-col tw:gap-8">
          {isCapped && (
            <div className="tw:flex tw:flex-col tw:gap-2">
              <Text variant="title-xs" weight="bold">
                Caps
              </Text>
              <PayoutCaps payoutTerm={payoutTerm} />
            </div>
          )}
          {isTiered && (
            <div className="tw:flex tw:flex-col tw:gap-2">
              <Text variant="title-xs" weight="bold">
                {isPoolPayout ? 'Multipliers' : 'Tiers'}
              </Text>
              <PayoutTiers payoutTerm={payoutTerm} />
            </div>
          )}
        </div>
      </TemporaryDrawer>
      <Card style={{ minHeight: index ? 'fit-content' : '257px' }}>
        <CardContent>
          <div className="tw:flex tw:justify-between tw:items-center">
            <div className="tw:flex tw:items-center tw:gap-2">
              <Text variant="title-xs" weight="bold">
                {`Reward ${indexText}`}
              </Text>
            </div>
            <PayoutTermMoreMenu conversion={conversion} payoutTerm={payoutTerm} />
          </div>

          <div className="tw:flex tw:flex-col tw:gap-4">
            <div className="tw:flex tw:gap-2">
              {isPoolPayout && (
                <Badge size="sm" className="tw:uppercase tw:rounded-full">
                  {payoutTerm.payee_type == PayeeType.Affiliate ? 'Affiliate' : 'End User'}
                </Badge>
              )}
              {!isPoolPayout && (
                <Tooltip content={tooltipTitle}>
                  <Badge size="sm" className="tw:uppercase tw:rounded-full">
                    {requireAffiliate ? 'Affiliate' : 'Incentives'}
                  </Badge>
                </Tooltip>
              )}
              {!isPoolPayout && isTiered && (
                <Badge size="sm" className="tw:uppercase tw:rounded-full">
                  Tiered
                </Badge>
              )}
              {!isPoolPayout && isMultilevel && (
                <Badge size="sm" className="tw:uppercase tw:rounded-full">
                  Multilevel
                </Badge>
              )}
              {!isPoolPayout && isCapped && (
                <Badge size="sm" className="tw:uppercase tw:rounded-full">
                  Capped
                </Badge>
              )}
              {!isPoolPayout && isRequiredApproval && (
                <Tooltip content={tooltipRequire}>
                  <Badge size="sm" className="tw:uppercase tw:rounded-full">
                    Require approval
                  </Badge>
                </Tooltip>
              )}
            </div>

            <div className="tw:flex tw:flex-col tw:gap-2">
              <div className="tw:flex tw:flex-row tw:gap-1">
                <Text variant="body-md" weight="bold" as="span">
                  {isPoolPayout ? 'Pool' : isFixedPayment ? 'Fixed' : 'Variable'} Payout
                </Text>
                {(isPoolPayout || (!isPoolPayout && !isFixedPayment)) && (
                  <Text variant="caption" color="muted">
                    calculated from conversion{' '}
                    {isPoolPayout
                      ? payoutTerm.amount_source === 'volume'
                        ? 'volume or TVL'
                        : payoutTerm.amount_source
                      : payoutTerm.trigger_amount_source}
                  </Text>
                )}
              </div>

              {isTiered ? (
                isCurrencyLoading && !isPoints ? (
                  <Skeleton width={120} height={20} />
                ) : (
                  <div className="tw:flex tw:gap-[0.3rem]">
                    <div className="tw:w-5 tw:h-5 tw:relative">
                      {isPoints ? (
                        <Image src="/assets/svg/coin.svg" alt={payoutCurrencyAddress} fill />
                      ) : (
                        <CurrencyIcon logoUri={currency?.logoUri} name={currencySymbol} size={20} />
                      )}
                    </div>
                    <Text variant="body-md">{isPoints ? 'POINTS' : currencySymbol}</Text>
                  </div>
                )
              ) : isPoolPayout ? (
                <>
                  <Text variant="body-md" color="muted">
                    {payeeType === PayeeType.Affiliate ? 'To referrers' : 'To end users'}
                  </Text>
                  {isCurrencyLoading && !isPointsReward ? (
                    <Skeleton width={120} height={20} />
                  ) : (
                    <FormattedAmount
                      {...{
                        amount: payoutTerm.pool_amount,
                        userType: 'pool',
                        payoutCurrencyAddress,
                        payoutCurrencyDecimals,
                        payoutCurrencySymbol,
                        payoutCurrencyLogoUri,
                        payoutBaseCurrencySymbol,
                        payoutCurrencyChainId,
                        isFixedPayment: true,
                        isPointsReward,
                        hasPayoutBaseCurrency,
                        width: '220px',
                        poolDuration: pool_duration,
                        poolCalculationDay: pool_calculation_day_cron as string,
                      }}
                    />
                  )}
                </>
              ) : (
                <PayoutAmounts payoutTerm={payoutTerm} />
              )}
            </div>

            {hasExtraInfo && (
              <Button
                variant="ghost"
                className="tw:w-fit tw:px-0 tw:font-semibold tw:gap-2"
                onClick={() => setShowDrawer(true)}
              >
                <Layers3 />
                <Text variant="caption">{drawerButtonLabel}</Text>
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </>
  );
};

export default PayoutTermCard;
